import { Component, h, Host, Prop, State, Watch } from '@stencil/core';
import { store, Unsubscribe } from '@stencil/redux';
import { postItemDetails } from '../../../api';
import { RootState } from '../../../redux/reducers';
import { ShortArticle } from '../../../types/types';
import { callIfDepsChanged } from '../../../utils/utils';
import { RentwareArticleBooking } from '../rentware-article-booking';
import { makeShortArticle } from '../utils/utils';
import { BookingDetails, ItemDetails, Widget } from '../../../redux/types';
import { openDetails, openCart, changeBookingDetails } from '../../../redux/actions';
import { t } from '../../../redux/utils';
import { checkSameDay } from '../../rtr-article-details/utils/picker';

@Component({
  tag: 'rentware-article-booking-cards-engine',
  shadow: false,
})
export class RentwareArticleBookingCardsEngine {
  @Prop() articleId: string;
  @Prop() bookingDetails: BookingDetails;

  @Prop({ mutable: true }) locale: string;
  @Prop({ mutable: true }) widget: Widget;

  @State() itemDetails: ItemDetails;

  unsubscribe!: Unsubscribe;
  openDetails!: typeof openDetails;
  openCart!: typeof openCart;
  changeBookingDetails!: typeof changeBookingDetails;

  componentWillLoad() {
    store.mapDispatchToProps(this, {
      openCart,
      openDetails,
      changeBookingDetails,
    });

    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      if (state.app.locale) {
        callIfDepsChanged(
          RentwareArticleBooking.name + '-postItemDetails-' + this.articleId,
          () => this.getItemDetails(state.bookingDetails),
          [state.orderDetails?.id, state.app.locale],
          !this.itemDetails, // if state is empty call anyway
        );
      }

      return {
        bookingDetails: state.bookingDetails,
        locale: state.app.locale,
        widget: state.widget,
      };
    });
  }

  disconnectedCallback() {
    this.unsubscribe();
  }

  @Watch('bookingDetails')
  bookingDetailsChanged(newDetails: BookingDetails, oldDetails: BookingDetails) {
    if (
      !checkSameDay(newDetails.fromDate, oldDetails?.fromDate) ||
      !checkSameDay(newDetails.toDate, oldDetails?.toDate) ||
      newDetails.fromTime !== oldDetails?.fromTime ||
      newDetails.toTime !== oldDetails?.toTime ||
      newDetails.selectedTimeSlot !== oldDetails?.selectedTimeSlot
    ) {
      if (!this.isDateReady) {
        return null;
      }

      this.getItemDetails(newDetails);
    } else if (
      // Always reload items for people change, order details created/removed, cart changed
      newDetails.peopleNumber !== oldDetails.peopleNumber ||
      newDetails.orderDetails?.id !== oldDetails.orderDetails?.id ||
      newDetails.cart?.items?.reduce((resultString: string, cartItem) => {
        return resultString + cartItem.id + cartItem.qty;
      }, '') !==
        oldDetails.cart?.items?.reduce((resultString: string, cartItem) => {
          return resultString + cartItem.id + cartItem.qty;
        }, '')
    ) {
      this.getItemDetails(newDetails);
    }
  }

  getItemDetails = (newDetails?: BookingDetails) => {
    const details = {
      ...this.bookingDetails,
      ...newDetails,
    };

    postItemDetails(
      this.articleId,
      details.orderDetails?.id,
      this.locale,
      null,
      false,
      data => {
        this.itemDetails = data;

        if (this.itemDetails.variation && !this.selectedVariationId) {
          this.changeBookingDetails({
            selectedVariationIds: {
              ...this.bookingDetails.selectedVariationIds,
              [this.itemDetails.id]: this.itemDetails.variation.items[0].id,
            },
          });
        }
      },
      undefined,
    );
  };

  handleNotAvailableButtonClick = () => {
    this.openDetails(this.articleId);
  };

  get isDateReady(): boolean {
    if (this.widget?.timePicker) {
      return !!this.bookingDetails.fromDate && !!this.bookingDetails.selectedTimeSlot;
    } else {
      if (!this.widget?.timePickerEnabled) {
        return !!this.bookingDetails.toDate;
      } else {
        return !!this.bookingDetails.toTime;
      }
    }
  }

  get selectedVariationId() {
    return this.bookingDetails.selectedVariationIds[this.itemDetails?.id];
  }

  get article(): ShortArticle {
    return makeShortArticle(this.itemDetails);
  }

  get hasSelectedVariationInCart(): boolean {
    return !!this.article.variation?.items.find(varItem => varItem.id === this.selectedVariationId && varItem.quantityInCart > 0);
  }

  get articleWithState(): ShortArticle {
    return {
      ...this.article,
      selectedVariationId: this.selectedVariationId,
      hasSelectedVariationInCart: this.hasSelectedVariationInCart,
    };
  }

  get statusInfo(): string {
    if (this.article.hasSelectedVariationInCart || (!this.article.variation && this.article.quantityInCart > 0)) {
      return <rentware-expiration-timer />;
    }

    if (!this.article.available && this.bookingDetails?.orderDetails?.id) {
      return t('search.notAvailable');
    }

    return t('articleBooking.bookNow');
  }

  get statusAction() {
    if (this.article.hasSelectedVariationInCart || (!this.article.variation && this.article.quantityInCart > 0)) {
      return this.openCart;
    }

    if (!this.article.available && this.bookingDetails?.orderDetails?.id) return;

    return () => this.openDetails(this.article.id, true);
  }

  render() {
    if (!this.bookingDetails || !this.widget || !this.itemDetails) {
      return null;
    }

    return (
      <Host class="CalendarTemplateContainerDimensions">
        <rtr-short-article-card article={this.article} statusInfo={this.statusInfo} onClickForDetails={this.openDetails} onStatusInfoAction={this.statusAction} />
      </Host>
    );
  }
}
